import flowRuleApi from '@/api/config/flowRuleApi'
import flowSensorApi from '@/api/config/flowSensorApi'
import flowValveApi from '@/api/config/flowValveApi'
import type { FlowSensorType, FlowValveType } from '@/types/hydraulic'
import type { WaterSourceUsage } from '@/features/config/hooks/watersource/useWaterSourceUsage'

/**
 * SCRUM-1032 — deleting a flow rule, and everything hanging off it.
 *
 * The API refuses to delete a rule that still has valves or sensors on it, and
 * refuses to delete a pipe that still has a rule. Left to the user that is a
 * scavenger hunt: delete four valves, then two sensors, then the rule, then the
 * pipe — in that order, guessing the order from a string of rejections.
 *
 * So the app does it, and says up front what it is about to remove. The order
 * is not arbitrary: a valve points at a sensor (`flowSensorId`), so the valves
 * go first, then the sensors, then the rule itself.
 *
 * Sequentially, not in parallel: these endpoints answer 500 under concurrent
 * writes, the same way the decoder writes do.
 */

export interface FlowRuleChildren {
  valves: FlowValveType[]
  sensors: FlowSensorType[]
}

export const NO_FLOW_RULE_CHILDREN: FlowRuleChildren = { valves: [], sensors: [] }

/** What a rule would take with it — for the confirmation, and for the delete. */
export const fetchFlowRuleChildren = async (
  interfaceId: number | string,
  flowRuleId: number | string,
): Promise<FlowRuleChildren> => {
  const id = Number(flowRuleId)
  if (!Number.isFinite(id) || id <= 0) return NO_FLOW_RULE_CHILDREN

  // Both endpoints list the whole interface; neither takes a rule id. Every
  // page, not the default 10: a child past the first page was neither named
  // in the confirmation nor deleted, and was left on a deleted rule
  // (SCRUM-1032 review). `PageSize: 0` is the API's "all rows".
  const everything = { interfaceId: Number(interfaceId), PageIndex: 1, PageSize: 0 }
  const [valveRes, sensorRes] = await Promise.all([
    flowValveApi.getAll(everything as any),
    flowSensorApi.getAll(everything as any),
  ])

  const valves: FlowValveType[] = (valveRes as any)?.data?.data ?? []
  const sensors: FlowSensorType[] = (sensorRes as any)?.data?.data ?? []

  return {
    valves: valves.filter((valve) => Number(valve.flowRuleId) === id),
    sensors: sensors.filter((sensor) => Number(sensor.flowRuleId) === id),
  }
}

/**
 * Removes a rule's valves and sensors, then the rule. `children` can be passed
 * in when the caller has already looked them up for the confirmation, so the
 * list the user agreed to is the list that is deleted.
 */
export const deleteFlowRuleWithChildren = async (
  interfaceId: number | string,
  flowRuleId: number | string,
  children?: FlowRuleChildren,
) => {
  const { valves, sensors } =
    children ?? (await fetchFlowRuleChildren(interfaceId, flowRuleId))

  for (const valve of valves) {
    await flowValveApi.delete(String(interfaceId), String(valve.id))
  }
  for (const sensor of sensors) {
    await flowSensorApi.delete(String(interfaceId), String(sensor.id))
  }

  await flowRuleApi.delete(String(interfaceId), String(flowRuleId))
}

/**
 * Why a cascade cannot go ahead: a valve or sensor it would remove is one a
 * water source opens or meters through. The valve and sensor grids refuse
 * those one at a time; deleting the pipe or its rule removed them all without
 * asking (SCRUM-1032 review). Valves first, as the delete takes them.
 *
 * Returns the translation key and the water sources to name, or null.
 */
export const findWaterSourceBlock = (
  { valves, sensors }: FlowRuleChildren,
  usage: Pick<WaterSourceUsage, 'valveUsedBy' | 'sensorUsedBy'>,
): {
  key: 'flowValveUsedByWaterSource' | 'flowSensorUsedByWaterSource'
  names: string[]
} | null => {
  const namesFor = (ids: number[], usedBy: Map<number, string[]>) =>
    Array.from(new Set(ids.flatMap((id) => usedBy.get(Number(id)) ?? [])))

  const valveNames = namesFor(
    valves.map((valve) => valve.id),
    usage.valveUsedBy,
  )
  if (valveNames.length) {
    return { key: 'flowValveUsedByWaterSource', names: valveNames }
  }
  const sensorNames = namesFor(
    sensors.map((sensor) => sensor.id),
    usage.sensorUsedBy,
  )
  if (sensorNames.length) {
    return { key: 'flowSensorUsedByWaterSource', names: sensorNames }
  }
  return null
}

/**
 * Valves and pumps live in one list and one column, but they are different
 * things to lose: the confirmation counts them separately. A row's `type` says
 * which it is — 0 pump, 3 booster pump, 1 and 2 the two master valve wirings.
 */
export const splitValvesAndPumps = (valves: FlowValveType[]) => {
  const pumps = valves.filter(
    (valve) => Number(valve?.type) === 0 || Number(valve?.type) === 3,
  ).length
  return { pumps, valves: valves.length - pumps }
}

const plural = (count: number, word: string) =>
  `${count} ${word}${count === 1 ? '' : 's'}`

/** How the confirmation names what is about to go with it. */
export const describeFlowRuleChildren = ({
  valves,
  sensors,
}: FlowRuleChildren): string => {
  const counts = splitValvesAndPumps(valves)
  const parts: string[] = []
  if (counts.valves) parts.push(plural(counts.valves, 'valve'))
  if (counts.pumps) parts.push(plural(counts.pumps, 'pump'))
  if (sensors.length) parts.push(plural(sensors.length, 'flow sensor'))
  if (parts.length === 0) return ''
  return parts.join(' and ')
}
